import React, { forwardRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMergeRefs } from '@floating-ui/react';
import { cx } from './classNames';

// The app's checkbox glyph (the retired MUI ModernCheckbox's): an outlined
// rounded square, filled with the current colour and a white mark when
// checked or mixed.
function CheckboxGlyph({ state }) {
    const unchecked = state === 'unchecked';
    return (
        <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            aria-hidden="true"
            focusable="false"
            className="pointer-events-none block"
        >
            <rect
                x={unchecked ? 1 : 0.75}
                y={unchecked ? 1 : 0.75}
                width={unchecked ? 16 : 16.5}
                height={unchecked ? 16 : 16.5}
                rx="4"
                fill={unchecked ? 'none' : 'currentColor'}
                stroke="currentColor"
                strokeWidth="1.5"
            />
            {state === 'checked' && (
                <path
                    d="M4.75 9.25L7.5 12L13.25 6"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            )}
            {state === 'indeterminate' && (
                <line x1="5" y1="9" x2="13" y2="9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
            )}
        </svg>
    );
}

CheckboxGlyph.propTypes = {
    state: PropTypes.oneOf(['unchecked', 'checked', 'indeterminate']).isRequired,
};

// MUI's default warning palette (main, then dark on hover); the app themes
// define no warning main of their own.
const WARNING_CLASS_NAME = 'text-[#ed6c02] hover:bg-[#ed6c02]/[0.08] hover:text-[#e65100] '
    + 'dark:text-[#ffa726] dark:hover:bg-[#ffa726]/[0.08] dark:hover:text-[#f57c00]';

/**
 * A controlled checkbox in the app's rounded-square look: a native
 * <input type="checkbox"> (so roles, labels, Space and form behaviour are
 * the browser's) laid invisibly over the glyph in a 32px target.
 * `indeterminate` shows the mixed state and sets the input's
 * `indeterminate` property, which assistive technology reads as "mixed".
 * `tone="warning"` draws it in MUI's warning orange in every state (a
 * draft choice not saved yet). Other props (aria-label, onChange, onClick,
 * name…) go to the input.
 */
const Checkbox = forwardRef(function Checkbox({
    checked = false,
    indeterminate = false,
    disabled = false,
    tone = 'accent',
    className,
    ...inputProps
}, ref) {
    // The DOM property has no attribute; the ref callback changes with it, so
    // React hands it the input again whenever it flips.
    const applyIndeterminate = useCallback((input) => {
        if (input) input.indeterminate = indeterminate;
    }, [indeterminate]);
    const inputRef = useMergeRefs([ref, applyIndeterminate]);
    let state = 'unchecked';
    if (indeterminate) state = 'indeterminate';
    else if (checked) state = 'checked';
    return (
        <span
            className={cx(
                'relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2',
                'has-[:focus-visible]:outline-current',
                disabled && 'text-ui-disabled-text',
                !disabled && tone === 'warning' && WARNING_CLASS_NAME,
                !disabled && tone === 'accent' && state === 'unchecked'
                    && 'text-content-secondary hover:bg-accent/[0.08] hover:text-accent',
                !disabled && tone === 'accent' && state !== 'unchecked' && 'text-accent hover:bg-accent/[0.08]',
                className,
            )}
        >
            <input
                ref={inputRef}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                className={cx(
                    'absolute inset-0 m-0 h-full w-full appearance-none opacity-0',
                    disabled ? 'cursor-default' : 'cursor-pointer',
                )}
                {...inputProps}
            />
            <CheckboxGlyph state={state} />
        </span>
    );
});

Checkbox.propTypes = {
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    disabled: PropTypes.bool,
    tone: PropTypes.oneOf(['accent', 'warning']),
    className: PropTypes.string,
};

export default Checkbox;
